import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { motion } from "framer-motion";
import { fetchMarketData, fetchFoodsData, fetchCategoryData } from "services/backendCalls";
import { fetchPartners } from "DBServices/firestoreDBService";
import TailwindModal from "components/modals/TailwindModal.js";

// ── Full-screen overlay dialog ────────────────────────────────────────────────
const Overlay = tw.div`fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col`;
const DialogHeader = tw.div`bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0`;
const DialogTitle = tw.h2`text-lg font-bold text-gray-800`;
const CloseBtn = tw.button`text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none`;
const DialogBody = tw.div`flex-1 overflow-y-auto`;


// ── Toolbar ───────────────────────────────────────────────────────────────────
const Toolbar = tw.div`flex flex-wrap gap-3 mb-5 items-center`;
const TabsRow = tw.div`flex gap-2`;
const TabBtn = styled.button`
  ${tw`px-4 py-2 rounded-full text-sm font-semibold transition`}
  ${props => props.active
    ? tw`bg-green-600 text-white shadow`
    : tw`bg-white text-gray-600 border border-gray-300 hover:border-green-400`}
`;
const SearchBox = tw.input`flex-1 min-w-0 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-green-500 bg-white`;

// ── Breadcrumb ────────────────────────────────────────────────────────────────
const Breadcrumb = tw.button`mb-4 text-sm font-semibold text-green-700 hover:text-green-900 flex items-center gap-1`;

// ── Card grid (mirrors marketplace) ──────────────────────────────────────────
const Grid = tw.div`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4`;

const Card = styled(motion.div)`
  ${tw`bg-white rounded-xl shadow-sm border-2 cursor-pointer overflow-hidden transition-all`}
  ${props => props.selected ? tw`border-green-500` : tw`border-transparent hover:border-green-300 hover:shadow-md`}
`;
const CardImg = tw.img`w-full h-32 object-cover`;
const CardImgPlaceholder = tw.div`w-full h-32 bg-gray-200 flex items-center justify-center text-gray-400 text-xs`;
const CardBody = tw.div`p-3`;
const CardTitle = tw.p`text-xs font-bold text-gray-800 leading-tight mb-1`;
const CardPrice = tw.p`text-xs font-semibold text-green-600`;
const SelectedBadge = tw.div`absolute top-auto right-1/2 bg-green-500 text-white text-xs font-bold px-2 py-0 rounded-full`;

// ── Footer ────────────────────────────────────────────────────────────────────
const DialogFooter = tw.div`bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0`;
const ChipRow = tw.div`flex flex-wrap gap-2 flex-1 mr-4`;
const Chip = tw.div`flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full`;
const ChipRemove = tw.button`text-green-600 hover:text-red-500 font-bold ml-1`;
const DoneBtn = tw.button`bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-2 rounded-lg text-sm flex-shrink-0`;

const EmptyState = tw.div`col-span-5 text-center text-gray-400 py-16 text-sm italic`;
const LoadingState = tw.div`col-span-5 text-center text-gray-500 py-16 text-sm`;

/**
 * MarketplaceSelector
 * Opens as a full-screen marketplace-style grid dialog with three tabs that
 * mirror the homepage / marketplace sections: Market, Category, Foods.
 *  - Market tab lists markets; clicking a market drills into that market's foods.
 *  - Category tab lists categories; clicking a category filters foods by it.
 *  - Foods tab lists all foods.
 * Selecting a FOOD opens the swipe dialog (type/measure/quantity). Markets and
 * categories are navigational, never added as products themselves.
 */
export default function MarketplaceSelector({ selectedProducts = [], onDone, onClose }) {
    const [marketItems, setMarketItems] = useState([]);
    const [foodItems, setFoodItems] = useState([]);
    const [categoryItems, setCategoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("market");
    const [search, setSearch] = useState("");

    // Drill-down state for Market / Category tabs
    const [drillMarket, setDrillMarket] = useState(null);
    const [drillCategory, setDrillCategory] = useState(null);

    // Local working copy so user can add/remove without committing until Done
    const [working, setWorking] = useState(selectedProducts);

    // Swipe dialog state
    const [swipeProduct, setSwipeProduct] = useState(null);
    const [swipeCategory, setSwipeCategory] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [markets, foods, categories, partners] = await Promise.all([
                    fetchMarketData(),
                    fetchFoodsData(),
                    fetchCategoryData(),
                    fetchPartners()
                ]);

                // Mirror the Market tab composition used on the homepage / marketplace
                // (loadStocksFromFirestore): static stocks_markets PLUS mapped partner
                // markets (approved partners that actually have approved foods).
                const approvedFoods = (foods || []).filter(f => f.status === 'approved' || typeof f.status === 'undefined');
                let finalMarkets = markets || [];
                if (approvedFoods.length > 0 && partners && partners.length > 0) {
                    const partnerMarkets = partners.filter(p => p.verificationStatus === 'approved' && !(p.partnerType && p.partnerType !== 'market'));
                    const partnerMarketsWithFoods = partnerMarkets.filter(p => approvedFoods.some(f => f.partnerMarketId === p.firestoreId));
                    const mappedPartnerMarkets = partnerMarketsWithFoods.map(p => ({
                        firestoreId: p.firestoreId,
                        title: p.businessName,
                        queryBy: p.businessName,
                        imageSrc: p.businessImage || p.imageSrc || '',
                        content: p.description || p.businessDescription || 'Partner Market',
                        rating: p.rating || 0,
                        reviews: p.reviews || 0,
                        isPartnerMarket: true
                    }));
                    finalMarkets = [...finalMarkets, ...mappedPartnerMarkets];
                }

                setMarketItems(finalMarkets);
                setFoodItems(foods || []);
                setCategoryItems(categories || []);
            } catch (e) {
                console.error("MarketplaceSelector load error:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Determine what kind of items we are listing and the active tab highlight
    const isFoodView = !!drillMarket || !!drillCategory || tab === "food";
    // Active tab highlight — once drilled into a market/category we are
    // viewing foods, so keep the Foods tab highlighted.
    const activeTab = (drillMarket || drillCategory) ? "food" : tab;

    const listingItems = drillMarket
        ? foodItems.filter(f =>
            (f.partnerMarketId && drillMarket.firestoreId && f.partnerMarketId === drillMarket.firestoreId) ||
            (f.marketId != null && drillMarket.id != null && f.marketId === drillMarket.id)
        )
        : drillCategory
            ? foodItems.filter(f =>
                (Array.isArray(f.categories) && f.categories.includes(drillCategory.firestoreId)) ||
                f.category === drillCategory.title
            )
            : tab === "market"
                ? marketItems
                : tab === "category"
                    ? categoryItems
                    : foodItems;

    const filtered = search.trim()
        ? listingItems.filter(i => (i.title || i.name || "").toLowerCase().includes(search.toLowerCase()))
        : listingItems;

    const getSelected = (product) => {
        const id = product.firestoreId || `${tab}_${product.id}`;
        return working.find(p => p.productId === id);
    };

    const handleCardClick = (product) => {
        if (isFoodView) {
            // Food: toggle selection or open swipe dialog
            const id = product.firestoreId || `${tab}_${product.id}`;
            if (working.find(p => p.productId === id)) {
                setWorking(prev => prev.filter(p => p.productId !== id));
            } else {
                setSwipeProduct(product);
                setSwipeCategory(drillCategory ? drillCategory.title : "food");
            }
        } else if (tab === "market") {
            // Market: drill into its foods
            setDrillMarket(product);
            setSearch("");
        } else if (tab === "category") {
            // Category: filter foods by this category
            setDrillCategory(product);
            setSearch("");
        }
    };

    const handleSwipeAdd = (item) => {
        const basket = item.basket?.[0];
        if (!basket || !swipeProduct) return;
        const id = swipeProduct.firestoreId || `${swipeCategory}_${swipeProduct.id}`;
        const newProduct = {
            productId: id,
            productName: swipeProduct.title || swipeProduct.name,
            productImage: swipeProduct.imageSrc || "",
            unitPrice: basket.price,
            measureUnit: basket.measure,
            quantity: basket.quantity,
            category: swipeCategory,
            type: basket.type
        };
        setWorking(prev => [...prev.filter(p => p.productId !== id), newProduct]);
        setSwipeProduct(null);
    };

    const listingLabel = drillMarket
        ? `Foods in ${drillMarket.title || drillMarket.name}`
        : drillCategory
            ? `Foods in ${drillCategory.title}`
            : tab === "market"
                ? "Select a market to view its foods"
                : tab === "category"
                    ? "Select a category to view its foods"
                    : "All foods";

    return (
        <>
            <Overlay onClick={onClose}>
                <div
                    style={{ display: "flex", flexDirection: "column", height: "100%", maxWidth: "1100px", width: "100%", margin: "0 auto" }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <DialogHeader>
                        <div>
                            <DialogTitle>🛒 Browse Marketplace</DialogTitle>
                            <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 2 }}>
                                {isFoodView
                                    ? "Click a food to select type, measure & quantity for your co-op group"
                                    : "Pick a market or category to browse its foods"}
                            </p>
                        </div>
                        <CloseBtn type="button" onClick={onClose}>×</CloseBtn>
                    </DialogHeader>

                    {/* Toolbar */}
                    <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "0.75rem 1.5rem", flexShrink: 0 }}>
                        <Toolbar>
                            <TabsRow>
                                <TabBtn type="button" active={activeTab === "market"} onClick={() => { setTab("market"); setDrillMarket(null); setDrillCategory(null); }}>
                                    Markets {marketItems.length > 0 && `(${marketItems.length})`}
                                </TabBtn>
                                <TabBtn type="button" active={activeTab === "category"} onClick={() => { setTab("category"); setDrillMarket(null); setDrillCategory(null); }}>
                                    Categories {categoryItems.length > 0 && `(${categoryItems.length})`}
                                </TabBtn>
                                <TabBtn type="button" active={activeTab === "food"} onClick={() => { setTab("food"); setDrillMarket(null); setDrillCategory(null); }}>
                                    Foods {foodItems.length > 0 && `(${foodItems.length})`}
                                </TabBtn>
                            </TabsRow>
                            <SearchBox
                                type="text"
                                placeholder={`Search ${isFoodView ? "food" : activeTab} items...`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </Toolbar>
                    </div>

                    {/* Breadcrumb when drilled in */}
                    {(drillMarket || drillCategory) && (
                        <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb", padding: "0.5rem 1.5rem", flexShrink: 0 }}>
                            <Breadcrumb type="button" onClick={() => { setDrillMarket(null); setDrillCategory(null); setSearch(""); }}>
                                ← Back to {drillMarket ? "Markets" : "Categories"}
                            </Breadcrumb>
                        </div>
                    )}

                    {/* Grid body */}
                    <DialogBody style={{ flex: 1, overflowY: "auto", background: "#f3f4f6", padding: "1.5rem" }}>
                        <p style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.75rem", fontWeight: 600 }}>{listingLabel}</p>
                        <Grid>
                            {loading ? (
                                <LoadingState>Loading marketplace products...</LoadingState>
                            ) : filtered.length === 0 ? (
                                <EmptyState>No {isFoodView ? "foods" : activeTab + "s"} found{search ? ` for "${search}"` : ""}.</EmptyState>
                            ) : (
                                filtered.map((product) => {
                                    const sel = isFoodView ? getSelected(product) : null;
                                    return (
                                        <Card
                                            key={product.firestoreId || product.id}
                                            selected={!!sel}
                                            onClick={() => handleCardClick(product)}
                                            style={{ position: "relative" }}
                                            whileHover={{ y: -2 }}
                                        >
                                            {sel && <SelectedBadge>✓</SelectedBadge>}
                                            {product.imageSrc
                                                ? <CardImg src={product.imageSrc} alt={product.title} />
                                                : <CardImgPlaceholder>No image</CardImgPlaceholder>
                                            }
                                            <CardBody>
                                                <CardTitle>{product.title || product.name}</CardTitle>
                                                {isFoodView ? (
                                                    <CardPrice>From ₦{parseFloat(product.price || 0).toLocaleString()}</CardPrice>
                                                ) : (
                                                    <CardPrice>{drillMarket || drillCategory ? "View foods →" : "Browse →"}</CardPrice>
                                                )}
                                                {sel && (
                                                    <div style={{ marginTop: 4, fontSize: "0.65rem", color: "#059669", fontWeight: 600 }}>
                                                        {sel.quantity} {sel.measureUnit} · ₦{(sel.unitPrice * sel.quantity).toLocaleString()}
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    );
                                })
                            )}
                        </Grid>
                    </DialogBody>

                    {/* Footer with chips + Done */}
                    <DialogFooter>
                        <ChipRow>
                            {working.length === 0 ? (
                                <span style={{ fontSize: "0.8rem", color: "#9ca3af" }}>No products selected yet</span>
                            ) : (
                                working.map(p => (
                                    <Chip key={p.productId}>
                                        {p.productName} · {p.quantity} {p.measureUnit}
                                        <ChipRemove
                                            type="button"
                                            onClick={() => setWorking(prev => prev.filter(x => x.productId !== p.productId))}
                                        >×</ChipRemove>
                                    </Chip>
                                ))
                            )}
                        </ChipRow>
                        <DoneBtn type="button" onClick={() => onDone(working)}>
                            Done {working.length > 0 && `(${working.length})`}
                        </DoneBtn>
                    </DialogFooter>
                </div>
            </Overlay>

            {/* Swipe dialog for type/measure/quantity */}
            {swipeProduct && (
                <TailwindModal
                    showModal={!!swipeProduct}
                    setShowModal={(v) => { if (!v) setSwipeProduct(null); }}
                    product={swipeProduct}
                    addItemToBasket={handleSwipeAdd}
                    items={[]}
                />
            )}
        </>
    );
}
